import type { IOption } from "@components/ui-kit";
import type { Attachment } from "@components/ui-kit/TextEditor/extensions/UploadAttachmentPlugin";
import { COMMUNICATION_TAB_QUERY_KEY } from "@components/widgets/Modals/ModalCommunication/components/tabs/communication-tab-query";
import type { TTaskTab } from "@components/widgets/Modals/ModalCommunication/components/tabs/TaskTab";
import { TaskModel } from "@components/widgets/Modals/ModalCommunication/models/Task";
import { TaskActionsStore } from "@components/widgets/Modals/ModalCommunication/store/actions-store";
import { TaskDescriptionStore } from "@components/widgets/Modals/ModalCommunication/store/description-store";
import { TaskDetailLayoutStore } from "@components/widgets/Modals/ModalCommunication/store/layout-store";
import { TaskPermissionsStore } from "@components/widgets/Modals/ModalCommunication/store/permissions-store";
import { TaskOverviewStore } from "@components/widgets/Modals/ModalCommunication/store/task-overview-store";
import type {
  FetchTaskOptions,
  TaskDetailOpenOptions,
} from "@components/widgets/Modals/ModalCommunication/store/types";
import type { Maybe } from "@graphql-tools/utils";
import { t } from "@lingui/macro";
import {
  type AssignableFile,
  CapacityAllocationTypeEnum,
  MarkTaskNotificationsAsReadDocument,
  type MarkTaskNotificationsAsReadMutation,
  type MarkTaskNotificationsAsReadMutationVariables,
  type SimpleTaskForClientFragment,
  type SimpleTaskFragment,
  type TaskComment,
  TaskDocument,
  TaskForScopedUsersDocument,
  type TaskForScopedUsersQuery,
  type TaskForScopedUsersQueryVariables,
  type TaskQuery,
  type TaskQueryVariables,
  UpdateTaskCurrentAssigneeDocument,
  type UpdateTaskCurrentAssigneeMutation,
  type UpdateTaskCurrentAssigneeMutationVariables,
  type User,
} from "@src/__generated__/graphql";
import { TASK_ID_QUERY_KEY, TaskTabIdEnum } from "@src/constants/tasks";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { trackEvent } from "@src/services/amplitude";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import type { ModalStore } from "@src/stores/ModalStore";
import { can } from "@src/utils/components/permissions";
import mapToOptions from "@src/utils/map-to-options";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { ValueState } from "@src/utils/mobx/states/ValueState";
import type { GraphQLFormattedError } from "graphql";
import { omit } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import router from "next/router";
import { createRef, type RefObject } from "react";
import { CommunicationTabParentStore } from "../components/tabs/communication-tab-parent-store";

export class TaskDetailModalStore implements BaseStore, ModalStore {
  appStore: AppStore;

  readonly modalId = "taskDetailModal";

  disclosureState = new DisclosureState<TaskDetailOpenOptions>({
    onOpen: (additionalData) => {
      trackEvent("task", "Opening task drawer");
      // Rendered by `Dialogs` while open: a store importing the modal UI
      // would put every task detail component in an import cycle with AppStore
      if (!additionalData?.id) return;
      this.showTask(additionalData.id);
      this.markNotificationsAsRead();

      if (!router.query[TASK_ID_QUERY_KEY]) {
        router.replace(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              [TASK_ID_QUERY_KEY]: additionalData.id,
            },
          },
          undefined,
          { shallow: true },
        );
      }

      // Warns about unsaved changes when the page is left
      window.addEventListener("beforeunload", this.handleBeforeUnload);
    },
    onClose: () => {
      window.removeEventListener("beforeunload", this.handleBeforeUnload);

      this.actions.reset();
      this.description.reset();
      this.handleChangeCommentInputValue("");
      this.setCommentChanged(false);
      router.replace(
        {
          pathname: router.pathname,
          query: omit(
            router.query,
            TASK_ID_QUERY_KEY,
            COMMUNICATION_TAB_QUERY_KEY,
            "commentId",
          ),
        },
        undefined,
        { shallow: true },
      );
    },
  });

  addClientDisclosure = new DisclosureState();
  addPartnerDisclosure = new DisclosureState();
  addScopedUserMenu = new DisclosureState();

  task = new ValueState<TaskModel | null>(null);
  taskStatuses = new ValueState<TaskQuery["taskStatuses"]>([]);
  taskPriorities = new ValueState<TaskQuery["taskPriorities"]>([]);
  activeTabId = new ValueState<TaskTabIdEnum>(TaskTabIdEnum.Description);
  taskId = new ValueState<string | undefined>(undefined);
  isLoading = new BooleanState();

  commentsContainerRef: RefObject<HTMLDivElement> = createRef();
  drawerBodyRef = createRef<HTMLDivElement>();

  @observable newlyCreatedCommentID?: TaskComment["id"] = undefined;
  seenLastComment = new BooleanState();

  @observable.ref
  commentChanged = false;

  @observable.ref
  commentInputValue = "";

  @observable.ref newTabId: TaskTabIdEnum | undefined = undefined;
  discardModal = new DisclosureState();
  discardChangesModal = new DisclosureState();

  readonly actions: TaskActionsStore;
  readonly communicationTabParentStore: CommunicationTabParentStore;
  readonly description: TaskDescriptionStore;
  readonly layout: TaskDetailLayoutStore;
  readonly overview: TaskOverviewStore;
  readonly permissions: TaskPermissionsStore;

  private internalUserTaskFetcher = new FetchHelper<
    TaskQuery,
    TaskQueryVariables
  >(TaskDocument);
  private scopedUserTaskFetcher = new FetchHelper<
    TaskForScopedUsersQuery,
    TaskForScopedUsersQueryVariables
  >(TaskForScopedUsersDocument);
  private markNotificationsAsReadMutator = new MutationHelper<
    MarkTaskNotificationsAsReadMutation,
    MarkTaskNotificationsAsReadMutationVariables
  >(MarkTaskNotificationsAsReadDocument);
  private updateCurrentAssigneeMutator = new MutationHelper<
    UpdateTaskCurrentAssigneeMutation,
    UpdateTaskCurrentAssigneeMutationVariables
  >(UpdateTaskCurrentAssigneeDocument);

  constructor(appStore: AppStore) {
    makeObservable(this);
    this.appStore = appStore;
    this.actions = new TaskActionsStore(appStore, this);
    this.communicationTabParentStore = new CommunicationTabParentStore(
      appStore,
    );
    this.description = new TaskDescriptionStore(appStore, this);
    this.layout = new TaskDetailLayoutStore(this);
    this.overview = new TaskOverviewStore(appStore, this);
    this.permissions = new TaskPermissionsStore(appStore, this);
  }

  /** Time was logged by hand from the header "+ Time". */
  @action.bound
  handleTimeTrackingItemCreated(trackedTime: number | undefined): void {
    void this.fetchTask({ silently: true });
    this.disclosureState.additionalData?.onTimeTrackingItemCreated?.(
      trackedTime,
    );
  }

  @computed
  get canStartTrackingTime(): boolean {
    if (!this.disclosureState.isOpen) return false;

    if (
      !this.appStore.workspaceStore.settings?.enable_allocation_meeting_play &&
      this.allocationType === CapacityAllocationTypeEnum.Meeting
    ) {
      return false;
    }

    return can(
      "timeTracking_create_own",
      "timeTracking_create_all",
      "timeTracking_create_team",
    );
  }

  @computed
  get hideTimeTrackingButton() {
    return (allocationType?: CapacityAllocationTypeEnum) =>
      !this.appStore.workspaceStore.settings?.enable_allocation_meeting_play &&
      (allocationType ?? this.allocationType) ===
        CapacityAllocationTypeEnum.Meeting;
  }

  @computed
  get allocationType(): CapacityAllocationTypeEnum | null {
    return this.disclosureState?.additionalData?.allocationType ?? null;
  }

  @action.bound
  setNewTabId(tabId: TaskTabIdEnum | undefined) {
    this.newTabId = tabId;
  }

  @action.bound handleChangeTab(index: number) {
    if (
      this.activeTabId.value === TaskTabIdEnum.Communication &&
      this.commentChanged
    ) {
      this.setNewTabId(index);
      this.discardModal.onOpen();
      return;
    }

    const taskTabId = this.taskTabs[index]?.id;
    if (typeof taskTabId !== "number") return;
    this.activeTabId.set(taskTabId);
  }

  @action.bound
  setCommentChanged(changed: boolean) {
    this.commentChanged = changed;
  }

  @computed get isInternalUser(): boolean {
    return this.appStore.authStore.isInternalUser;
  }

  @computed get priorityOptions(): IOption[] {
    return mapToOptions.priorities.toOptions(this.taskPriorities.value);
  }

  @action.bound
  async markNotificationsAsRead(): Promise<void> {
    if (!this.taskId.value) return;

    await this.markNotificationsAsReadMutator.mutate({
      task_id: this.taskId.value,
    });
  }

  @action.bound
  handleTaskUpdated(task: SimpleTaskFragment | SimpleTaskForClientFragment) {
    this.disclosureState.additionalData?.onChange?.(task);
    this.fetchTask();
  }

  @computed get hasUnsavedChanges(): boolean {
    return this.description.isChanged || this.commentChanged;
  }

  @action.bound
  requestClose(): void {
    if (this.hasUnsavedChanges) {
      this.discardChangesModal.onOpen();
      return;
    }

    this.disclosureState.onClose();
  }

  @computed get statusOptions() {
    return this.taskStatuses.value.map((status) => ({
      label: status.name,
      value: status.id,
    }));
  }

  @computed get clientIds() {
    return this.task.value?.clients.map((client) => client.id) ?? [];
  }

  @computed get partnerIds() {
    return this.task.value?.partners.map((partner) => partner.id) ?? [];
  }

  @computed
  get isOvertime() {
    return (spentTime?: string | number, plannedTime?: Maybe<number>) =>
      !!this.appStore.workspaceStore.settings?.highlight_overtime_in_tasks &&
      spentTime !== undefined &&
      typeof plannedTime === "number" &&
      Number(spentTime) > plannedTime;
  }

  /** Loads a task into the detail, from a clean state. */
  @action.bound
  private showTask(taskId: string): void {
    this.taskId.set(taskId);
    this.description.mode.set("read");
    this.communicationTabParentStore.reset();
    this.overview.reset();
    void this.fetchTask();

    // Mentionable users for the description (internal) and every
    // communication tab, so mentions are instant when switching tabs
    const { workspaceStore } = this.appStore;
    workspaceStore.getDefaultMentionsUsers(["internal"], taskId);
    for (const tab of this.communicationTabParentStore.availableTabs) {
      workspaceStore.getDefaultMentionsUsers(tab.userTypesWithAccess, taskId);
    }
  }

  /**
   * `silently` refreshes in the background: no spinner, and a failure keeps
   * the task open. `notifyOpener` passes the fresh task to whoever opened it.
   */
  @action.bound async fetchTask({
    silently = false,
    notifyOpener = false,
  }: FetchTaskOptions = {}) {
    const taskId = this.taskId.value;
    if (!taskId) return;
    if (!silently) {
      this.isLoading.on();
      // Loads alongside the task, a background refresh keeps the known value
      void this.permissions.load(taskId);
    }

    let data: TaskQuery | TaskForScopedUsersQuery | null = null;
    let error: Error | readonly GraphQLFormattedError[] | null = null;

    if (this.isInternalUser) {
      [data, error] = await this.internalUserTaskFetcher.fetch(
        { id: taskId },
        undefined,
        true,
      );
    } else {
      [data, error] = await this.scopedUserTaskFetcher.fetch(
        { id: taskId },
        undefined,
        true,
      );
    }

    // Another task was opened meanwhile, this response is stale
    if (taskId !== this.taskId.value) return;

    if (error || !data) {
      const isTaskDeleted =
        Array.isArray(error) &&
        error.some(
          (e) =>
            "message" in e &&
            e.message === "Validation failed for the field [taskPositionStat].",
        );

      if (isTaskDeleted) {
        this.appStore.UIStore.toast({
          status: "error",
          title: t`This task was deleted.`,
        });
      } else if (Array.isArray(error)) {
        for (const { message } of error) {
          this.appStore.UIStore.toast({ status: "error", title: message });
        }
      }

      // A failed background refresh keeps the task and unsaved texts open
      if (!silently || isTaskDeleted) this.disclosureState.onClose();
      this.isLoading.off();
      return;
    }

    this.task.set(
      new TaskModel(
        data.task,
        "taskPositionStat" in data ? data.taskPositionStat : undefined,
      ),
    );
    this.taskStatuses.set(data.taskStatuses);
    this.taskPriorities.set(data.taskPriorities);
    this.description.load(this.task.value?.description?.body, {
      keepDraft: silently,
    });
    if (notifyOpener) {
      this.disclosureState.additionalData?.onChange?.(data.task);
    }

    if (!silently) this.isLoading.off();
  }

  /** Lets the opener add the duplicate, then switches the detail to it. */
  @action.bound
  showDuplicate(task: SimpleTaskFragment): void {
    this.disclosureState.additionalData?.onDuplicate?.(task);
    // An opener may have reopened the detail on the duplicate already
    if (this.taskId.value === task.id) return;

    this.showTask(task.id);
  }

  @action.bound
  refetchMentionsUsers(): void {
    const taskId = this.taskId.value;
    if (!taskId) return;

    this.appStore.workspaceStore.invalidateMentionsCache(taskId);

    this.appStore.workspaceStore.getDefaultMentionsUsers(["internal"], taskId);
    for (const tab of this.communicationTabParentStore.availableTabs) {
      this.appStore.workspaceStore.getDefaultMentionsUsers(
        tab.userTypesWithAccess,
        taskId,
      );
    }
  }

  @computed
  get currentTabIndex() {
    return this.taskTabs.findIndex((tab) => tab.id === this.activeTabId.value);
  }

  @computed
  get taskTabs(): TTaskTab[] {
    return (
      [
        {
          id: TaskTabIdEnum.Description,
          label: () => t`Description`,
          icon: "file-02",
          userTypesWithAccess: ["internal", "client", "partner"],
        },
        this.layout.isDrawerMode && {
          id: TaskTabIdEnum.Communication,
          label: () => t`Communication`,
          icon: "message-chat-square",
          userTypesWithAccess: ["internal", "client", "partner"],
        },
        {
          id: TaskTabIdEnum.Attachments,
          label: () => t`Files`,
          icon: "paperclip",
          userTypesWithAccess: ["internal", "client", "partner"],
        },
        {
          id: TaskTabIdEnum.LinkedTasks,
          label: () => t`Linked tasks`,
          icon: "check-done-01",
          userTypesWithAccess: ["internal"],
        },
        {
          id: TaskTabIdEnum.Subtasks,
          label: () => t`Subtasks`,
          icon: "git-branch-01",
          userTypesWithAccess: ["internal"],
        },
      ] as TTaskTab[]
    )
      .filter(Boolean)
      .filter((tab) => {
        const userType = this.appStore.authStore.user?.type;
        return !!userType && tab.userTypesWithAccess.includes(userType);
      })
      .filter((tab) => {
        if (tab.id !== TaskTabIdEnum.Subtasks) return true;
        return !this.task.value?.parent;
      });
  }

  createAttachmentFromFile(
    file: Pick<AssignableFile, "public_id" | "filename" | "mime_type" | "urls">,
  ): Attachment {
    return {
      id: file.public_id,
      filename: file.filename,
      mime_type: file.mime_type,
      viewable: file.urls.viewable ?? undefined,
      original: file.urls.original ?? undefined,
      thumbnail: file.urls.thumbnail ?? undefined,
    };
  }

  @action.bound
  handleChangeCommentInputValue(value: string) {
    this.commentInputValue = value;
  }

  private handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!this.hasUnsavedChanges) return;

    const message = t`You have unsaved changes. Are you sure you want to leave the page?`;
    event.preventDefault();
    event.returnValue = message;
    return message;
  };

  @action.bound
  async setCurrentAssignee(userId: User["id"] | null): Promise<boolean> {
    const task = this.task.value;
    if (!task?.id) return false;
    if ((task.currentAssignee?.id ?? null) === userId) return false;

    const [, error] = await this.updateCurrentAssigneeMutator.mutate({
      taskId: task.id,
      userId,
    });
    if (error) return false;

    task.setCurrentAssignee(userId);
    return true;
  }
}
